import {
  FloatingPortal,
  Placement,
  autoUpdate,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles
} from "@floating-ui/react";
import { filterClasses } from "@swivl-mono/helpers";
import { memo, useState } from "react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

enum Outlet {
  WRAPPER = "wrapper",
  TOOLTIP = "tooltip"
}

interface TooltipProps extends ComponentPropsWithoutRef<"div"> {
  children: ReactNode;
  classNames?: { [Key in Outlet]?: string };
  content?: string;
  delay?: { open?: number; close?: number };
  placement?: Placement;
  tooltipOffset?: number;
  transitionDuration?: [number, number];
  zIndex?: number;
  disabled?: boolean;
}

const BaseTooltip = ({
  classNames = {},
  children,
  content,
  delay = { open: 0, close: 0 },
  transitionDuration = [200, 300],
  placement = "top",
  tooltipOffset = 1,
  zIndex = 700,
  disabled = false,
  ...rest
}: TooltipProps) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const { context, x, y, refs, strategy } = useFloating({
    open: isTooltipOpen,
    onOpenChange: setIsTooltipOpen,
    placement: placement,
    whileElementsMounted: autoUpdate,
    middleware: [shift(), offset(tooltipOffset)]
  });
  const { isMounted, styles } = useTransitionStyles(context, {
    duration: {
      open: transitionDuration[0],
      close: transitionDuration[1]
    },
    open: {
      opacity: 1
    },
    close: {
      opacity: 0
    }
  });
  const hover = useHover(context, {
    move: false,
    delay: {
      open: delay.open,
      close: delay.close
    }
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  const internalClassNames = {
    wrapper: filterClasses("inline-block w-fit", classNames.wrapper || ""),
    tooltip: filterClasses("w-fit max-w-60 rounded-md bg-grey-800 px-2 py-1 text-sm text-pretty break-words text-white", classNames.tooltip || "")
  };

  if (disabled) {
    return <>{children}</>;
  }

  return (
    <>
      <div className={internalClassNames.wrapper} {...getReferenceProps({ ref: refs.setReference })} {...rest}>
        {children}
      </div>
      <FloatingPortal>
        {isMounted && content && (
          <div
            {...getFloatingProps({
              ref: refs.setFloating
            })}
            className={internalClassNames.tooltip}
            style={{
              position: strategy,
              left: x ?? 0,
              top: y ?? 0,
              zIndex: zIndex,
              ...styles
            }}
          >
            {content}
          </div>
        )}
      </FloatingPortal>
    </>
  );
};

export const Tooltip = memo(BaseTooltip);
